import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import CustomStepBadge from '../../../components/OnboardingComponents/CustomStepBadge';
import CustomButton from '../../../components/OnboardingComponents/CustomButton';
import CustomEntryCard from '../../../components/OnboardingComponents/CustomEntryCard';
import OnboardingProjectForm from '../../../components/OnboardingComponents/OnboardingProjectForm';
import { ICONS } from '../../../components/OnboardingComponents/icon';
import useCategories from '../../../hooks/useCategories';
import { setFutureProjects } from '../../../store/slices/onboardingSlice';
import {
  MAX_ONBOARDING_PROJECTS,
  emptyOnboardingProject,
  createProjectSchema,
  collectOnboardingProjects,
  submitOnboardingProjects,
  updateOnboardingProject,
  deleteOnboardingProject,
  createOnboardingProjectFieldHandlers,
} from './onboardingProjectHelpers';

const ONBOARDING_STAGE = 'future';
const MAX_VIA_ADD_BUTTON = MAX_ONBOARDING_PROJECTS - 1;

/**
 * Step3FutureProjects
 * ------------------------------------------------------------------
 * Future ideas are optional — Next works with an empty list.
 * Any draft with content is validated before submit.
 * Up to 2 projects via "Add another"; the 3rd is taken from the form on Next.
 */
const Step3FutureProjects = ({ onNext, editId, fromReview }) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.onboarding?.futureProjects || []);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasLoadedEdit = useRef(false);
  const { categories, getCategoryLabel } = useCategories();

  const formik = useFormik({
    initialValues: emptyOnboardingProject(),
    validationSchema: createProjectSchema({
      dateMode: 'range',
      allowOngoing: false,
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      if (editingIndex !== null) {
        const next = items.map((it, i) => (i === editingIndex ? values : it));
        dispatch(setFutureProjects(next));
        setEditingIndex(null);
      } else {
        if (items.length >= MAX_VIA_ADD_BUTTON) return;
        dispatch(setFutureProjects([...items, values]));
      }
      formik.resetForm();
    },
  });

  const { setFieldValue, clearFieldErrorOnBlur } = useMemo(
    () => createOnboardingProjectFieldHandlers(formik),
    [formik]
  );

  const canAddAnother = items.length < MAX_VIA_ADD_BUTTON;
  const showAddButton = editingIndex === null && canAddAnother;

  const categoryLabels = (values) =>
    values.map(getCategoryLabel).filter(Boolean).join(', ');

  const handleEdit = useCallback((index) => {
    setEditingIndex(index);
    formik.setValues(items[index]);
  }, [formik, items, setEditingIndex]);

  useEffect(() => {
    if (!editId || hasLoadedEdit.current) return;
    const index = items.findIndex((it) => it.id === editId);
    if (index !== -1) {
      hasLoadedEdit.current = true;
      handleEdit(index);
    }
  }, [editId, items, handleEdit]);

  const handleRemove = async (index) => {
    const project = items[index];
    if (project?.id) {
      const deleted = await deleteOnboardingProject(project.id);
      if (!deleted) return;
    }
    const next = items.filter((_, i) => i !== index);
    dispatch(setFutureProjects(next));
    if (editingIndex === index) {
      setEditingIndex(null);
      formik.resetForm();
    }
  };

  const handleNext = async () => {
    const final = await collectOnboardingProjects({
      items,
      formik,
      editingIndex,
    });
    if (final === null) return;
    if (final.length > MAX_ONBOARDING_PROJECTS) return;

    const isEditing = editingIndex !== null;

    setIsSubmitting(true);
    try {
      if (isEditing && final[editingIndex]?.id) {
        const updated = await updateOnboardingProject({
          project: final[editingIndex],
          onboardingStage: ONBOARDING_STAGE,
        });
        if (updated) {
          dispatch(setFutureProjects(final));
          formik.resetForm();
          setEditingIndex(null);
          if (fromReview) onNext?.(final);
        }
        return;
      }

      await submitOnboardingProjects({
        projects: final,
        onboardingStage: ONBOARDING_STAGE,
        onSuccess: (projects) => {
          formik.resetForm();
          setEditingIndex(null);
          dispatch(setFutureProjects(projects));
          if (!isEditing || fromReview) onNext?.(projects);
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="amok-onboarding-step">
      <div className="amok-onboarding-step__header">
        <CustomStepBadge tone="info">Step 4 of 4</CustomStepBadge>
        <span className="amok-onboarding-step__count">
          {items.length} of {MAX_ONBOARDING_PROJECTS} added
        </span>
      </div>

      <h2 className="amok-onboarding-step__title">Future Projects</h2>
      <p className="amok-onboarding-step__subtitle">
        Add ideas you&rsquo;re planning &mdash; this step is optional
      </p>

      {items.length > 0 && (
        <div className="amok-onboarding-step__list">
          {items.map((p, i) => (
            <CustomEntryCard
              key={i}
              title={p.projectName}
              meta={[
                p.roleTitle,
                p.startDate && `Target ${p.startDate}`,
                p.endDate && `Finished ${p.endDate}`,
                categoryLabels(p.categories),
              ].filter(Boolean)}
              onEdit={() => handleEdit(i)}
              onDelete={() => handleRemove(i)}
              editIconSrc={ICONS.edit}
              deleteIconSrc={ICONS.delete}
            />
          ))}
        </div>
      )}

      <OnboardingProjectForm
        values={formik.values}
        errors={formik.errors}
        setFieldValue={setFieldValue}
        onFieldBlur={clearFieldErrorOnBlur}
        categoryOptions={categories}
        showOngoing={false}
        dateLabel="Target Dates"
        startPlaceholder="Target Start Date"
        endPlaceholder="Target Finished Date"
        namePlaceholder="e.g. Documentary Score"
        rolePlaceholder="e.g. Composer"
      />

      <div className="amok-onboarding-step__footer">
        <div className="amok-onboarding-step__footer-left">
          {showAddButton && (
            <CustomButton
              variant="secondary"
              size="md"
              iconSrc={ICONS.plus}
              disabled={
                !formik.values.projectName.trim() ||
                (editingIndex === null && !canAddAnother) ||
                isSubmitting
              }
              onClick={() => formik.submitForm()}
            >
              {editingIndex !== null ? 'Save project' : 'Add another project'}
            </CustomButton>
          )}
          <span className="amok-onboarding-step__hint">
            {items.length < MAX_ONBOARDING_PROJECTS
              ? `You can add up to ${MAX_ONBOARDING_PROJECTS} projects`
              : 'Maximum projects added'}
          </span>
        </div>
        <CustomButton
          variant="primary"
          size="md"
        trailingIconSrc={editingIndex !== null ? null : ICONS.arrowRight}
          onClick={handleNext}
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {isSubmitting
            ? 'Saving...'
            : editingIndex !== null
              ? 'Save'
              : 'Next'}
        </CustomButton>
      </div>
    </div>
  );
};

export default Step3FutureProjects;
